import type { BulkRunStatus } from '../../types/bulkOnboarding';

// ⚠️⚠️ THE BULK-RUN CHUNK LOOP, AND WHAT IT DOES WHEN A CHUNK'S RESPONSE IS LOST
// (2026-09-28). A 524 does not mean the chunk failed. Cloudflare answers 524 at
// ~100s while Cloud Run keeps working (--no-cpu-throttling), so the run's truth
// is in Firestore — the UI once showed "3/3 settled (done)" beside an error.
// A non-JSON response (a proxy's HTML error page) or a dropped connection is
// therefore a "we lost the answer", not an outcome: re-read the run and decide.
//
// ⚠️⚠️ AND NEVER CONTINUE WHILE THE LOST CHUNK IS STILL RUNNING. The server holds a
// chunk LEASE (bulkSteps.ts) and reports it as run.chunkInFlight. Calling
// /continue while it is live would ask for a second chunk over the same clients'
// Zernio writes; the server would refuse it (busy), but waiting is the honest
// client behaviour and costs nothing. Kept pure with injected I/O so every
// branch is asserted without jsdom.

export type BulkRunStepResponse = { run: BulkRunStatus; done: boolean; busy?: boolean };

/** A response that carried no JSON body — a proxy error page, not our API. */
export class NonJsonResponseError extends Error {
  constructor(public readonly status: number) {
    super(`The server's reply was lost (HTTP ${status}).`);
    this.name = 'NonJsonResponseError';
  }
}

/** Lost the answer rather than received a real error: re-read before judging. */
export function isTransportFailure(err: unknown): boolean {
  if (err instanceof NonJsonResponseError) return true;
  // fetch rejects with a TypeError when the connection itself fails.
  return err instanceof TypeError;
}

export type RecoveryDecision = 'done' | 'wait' | 'continue' | 'fail';

/**
 * After a lost response, what does the stored run say?
 * `priorRunStartedAt` is the startedAt of the run that existed BEFORE this drive
 * started a new one (null when resuming). If the stored run still has that
 * startedAt, our start request never created a run — the one we are reading is
 * an OLD run, and reading its "finished" as ours would report a success that
 * never happened. Compared by the server's own timestamp, so clock skew between
 * browser and server cannot matter.
 */
export function afterTransportFailure(
  run: BulkRunStatus | null,
  priorRunStartedAt: string | null,
): RecoveryDecision {
  if (!run || run.startedAt === null) return 'fail';
  if (priorRunStartedAt !== null && run.startedAt === priorRunStartedAt) return 'fail';
  if (run.finished) return 'done';
  if (run.chunkInFlight) return 'wait';
  return 'continue'; // active or interrupted — both are resumable
}

// ⚠️⚠️ WHAT THE PAGE SAYS WHILE A STEP HOLDS THE LEASE (2026-09-28). Not
// cosmetic: if an instance dies mid-chunk the run stays locked for the whole
// lease (~6.5 minutes) with nothing running, and a page reading "in progress"
// that long looks broken — an operator starts clicking, and clicking is how two
// chunks end up writing over each other. So it says what is happening and that
// no action is needed.
// ⚠️ It branches on whether THIS tab is driving, because only then is "it carries
// on by itself" true: an undriven run whose lease expires reads as interrupted
// and needs Resume. "A few minutes", not a figure — the lease length is a server
// constant, and a copy of it here would drift.
export function stepInFlightNotice(run: BulkRunStatus | null, driving: boolean): string | null {
  if (!run || run.finished || !run.chunkInFlight) return null;
  return driving
    ? 'Waiting for the current step to finish. If the server restarted mid-step this can take a few minutes — this page carries on by itself, so there is no need to click anything.'
    : 'Waiting for the current step to finish. If the server restarted mid-step it can take a few minutes before the run can be resumed — there is no need to click anything yet.';
}

export const RECOVERY_POLL_MS = 3_000;
export const BUSY_BACKOFF_MS = 3_000;
// Longer than the server's chunk lease (a lost chunk can hold it that long),
// after which the lease has expired and the run reads as resumable.
export const RECOVERY_GIVE_UP_MS = 8 * 60 * 1000;

export type DriveDeps = {
  continueRun: () => Promise<BulkRunStepResponse>;
  readRun: () => Promise<BulkRunStatus | null>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  onStep: (step: BulkRunStepResponse) => void;
};

async function recover(
  err: unknown,
  priorRunStartedAt: string | null,
  deps: DriveDeps,
): Promise<BulkRunStepResponse> {
  const giveUpAt = deps.now() + RECOVERY_GIVE_UP_MS;
  for (;;) {
    await deps.sleep(RECOVERY_POLL_MS);
    let run: BulkRunStatus | null;
    try {
      run = await deps.readRun();
    } catch (readErr) {
      if (!isTransportFailure(readErr) || deps.now() >= giveUpAt) throw err;
      continue; // the status read was lost too — keep trying until the cap
    }
    const decision = afterTransportFailure(run, priorRunStartedAt);
    if (decision === 'fail') throw err;
    if (decision === 'done') return { run: run!, done: true };
    if (decision === 'continue') return { run: run!, done: false };
    if (deps.now() >= giveUpAt) throw err; // 'wait', past the cap
  }
}

async function oneChunk(
  call: () => Promise<BulkRunStepResponse>,
  priorRunStartedAt: string | null,
  deps: DriveDeps,
): Promise<BulkRunStepResponse> {
  try {
    return await call();
  } catch (err) {
    if (!isTransportFailure(err)) throw err;
    return recover(err, priorRunStartedAt, deps);
  }
}

/** Drive the request-held chunk loop to completion. */
export async function driveBulkRun(
  start: () => Promise<BulkRunStepResponse>,
  priorRunStartedAt: string | null,
  deps: DriveDeps,
): Promise<BulkRunStepResponse> {
  let step = await oneChunk(start, priorRunStartedAt, deps);
  deps.onStep(step);
  while (!step.done) {
    // Another chunk holds the lease: it is doing the work. Don't spin.
    if (step.busy) await deps.sleep(BUSY_BACKOFF_MS);
    step = await oneChunk(deps.continueRun, priorRunStartedAt, deps);
    deps.onStep(step);
  }
  return step;
}
